'use client';

import * as React from 'react';
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subWeeks,
  subMonths,
  subDays,
  format,
} from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export type ClaimsDashboardDatePreset =
  | 'all'
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'custom';

export interface ClaimsDashboardDateRange {
  // yyyy-MM-dd, matches VehicleClaimListFilters.intimation_from/to.
  // null for the "All" preset — no intimation-date filter applied.
  from: string | null;
  to: string | null;
}

export interface ClaimsDashboardDateFilterProps {
  onChange: (range: ClaimsDashboardDateRange) => void;
  /** Overrides "now" for preset resolution — for tests; defaults to the real current time. */
  now?: Date;
}

const DATE_FORMAT = 'yyyy-MM-dd';

// Business-week convention for this dashboard's "This Week"/"Last Week" presets — Monday start.
const WEEK_OPTIONS = { weekStartsOn: 1 as const };

const PRESET_OPTIONS: Array<{ value: ClaimsDashboardDatePreset; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'custom', label: 'Custom Date Range' },
];

function fmt(date: Date): string {
  return format(date, DATE_FORMAT);
}

export function resolveDateRangePreset(
  preset: Exclude<ClaimsDashboardDatePreset, 'custom'>,
  referenceDate: Date = new Date(),
): ClaimsDashboardDateRange {
  switch (preset) {
    case 'all':
      return { from: null, to: null };
    case 'today': {
      const d = fmt(referenceDate);
      return { from: d, to: d };
    }
    case 'yesterday': {
      const d = fmt(subDays(referenceDate, 1));
      return { from: d, to: d };
    }
    case 'this_week':
      return {
        from: fmt(startOfWeek(referenceDate, WEEK_OPTIONS)),
        to: fmt(endOfWeek(referenceDate, WEEK_OPTIONS)),
      };
    case 'last_week': {
      const lastWeek = subWeeks(referenceDate, 1);
      return {
        from: fmt(startOfWeek(lastWeek, WEEK_OPTIONS)),
        to: fmt(endOfWeek(lastWeek, WEEK_OPTIONS)),
      };
    }
    case 'this_month':
      return {
        from: fmt(startOfMonth(referenceDate)),
        to: fmt(endOfMonth(referenceDate)),
      };
    case 'last_month': {
      const lastMonth = subMonths(referenceDate, 1);
      return {
        from: fmt(startOfMonth(lastMonth)),
        to: fmt(endOfMonth(lastMonth)),
      };
    }
  }
}

/** Preset dropdown (+ custom range) for the vehicle-claims dashboard; resolves to a concrete {from, to} on every change. */
export function ClaimsDashboardDateFilter({ onChange, now }: ClaimsDashboardDateFilterProps) {
  const [preset, setPreset] = React.useState<ClaimsDashboardDatePreset>('all');
  const [customFrom, setCustomFrom] = React.useState('');
  const [customTo, setCustomTo] = React.useState('');

  React.useEffect(() => {
    if (preset === 'custom') {
      if (customFrom && customTo) {
        onChange({ from: customFrom, to: customTo });
      }
      return;
    }
    onChange(resolveDateRangePreset(preset, now));
  }, [preset, customFrom, customTo, onChange, now]);

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
      <div className="flex flex-col gap-2">
        <label htmlFor="claims-dashboard-date-preset" className="text-sm font-medium text-text-primary">
          Date Range
        </label>
        <Select value={preset} onValueChange={(value) => setPreset(value as ClaimsDashboardDatePreset)}>
          <SelectTrigger id="claims-dashboard-date-preset" className="w-full sm:w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRESET_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {preset === 'custom' && (
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="flex flex-col gap-2">
            <label htmlFor="claims-dashboard-date-from" className="text-sm font-medium text-text-primary">
              From
            </label>
            <input
              id="claims-dashboard-date-from"
              type="date"
              value={customFrom}
              max={customTo || undefined}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="h-10 rounded-md border border-border-primary bg-background-primary px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="claims-dashboard-date-to" className="text-sm font-medium text-text-primary">
              To
            </label>
            <input
              id="claims-dashboard-date-to"
              type="date"
              value={customTo}
              min={customFrom || undefined}
              onChange={(e) => setCustomTo(e.target.value)}
              className="h-10 rounded-md border border-border-primary bg-background-primary px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
            />
          </div>
        </div>
      )}
    </div>
  );
}
